import React, { useState } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import MovementAssistanceBreakdown from './movement-assistance-breakdown';

// #region Styles
/**
 * Styling constants for the Movement Raw Data Table component
 */

/** Header cell styling - used for table column headers */
const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
};

/** Standard cell styling - used for table data cells */
const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '13px',
    color: '#1f2937',
};

/** Base table styling with full width, border collapse, and fixed layout */
const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
};

/** Fixed column widths to keep header and body tables aligned */
const columnWidths = ['4%', '12%', '12%', '9%', '9%', '12%', '10%', '10%', '9%', '11%'];

/** Shared colgroup to enforce consistent column widths across split tables */
const ColGroup = () => (
    <colgroup>
        {columnWidths.map((width, i) => (
            <col key={i} style={{ width }} />
        ))}
    </colgroup>
);

const VirtuosoTable = React.forwardRef(({ children, style, ...rest }, ref) => (
    <table ref={ref} {...rest} style={{ ...tableStyle, ...style }}>
        <ColGroup />
        {children}
    </table>
));

const rawDataTableComponents = { Table: VirtuosoTable };

/** Main section title styling (e.g., date headers) */
const sectionTitleStyle = {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '8px',
    marginTop: '20px',
};

/** Sub-section title styling (e.g., "Outbound", "Inbound") */
const subTitleStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '6px',
    marginTop: '14px',
};

/** Modal overlay - dims background */
const modalOverlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
};

/** Modal content box */
const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '320px',
    maxWidth: '90vw',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
};

// #endregion

// #region Components
/**
 * ManualOverrideModal Component
 * Modal that lets the user set movementOverride to true and patch the booking.
 *
 * @param {Object} props
 * @param {Object|null} props.row - The movement row (booking) being overridden
 * @param {boolean} props.open - Whether the modal is visible
 * @param {boolean} props.isPatching - Whether a patch is in progress (show loader)
 * @param {() => void} props.onClose - Called when modal is closed without applying
 * @param {(row: Object) => void} [props.onApply] - Called when user applies; dispatches patch (modal stays open until success)
 */
const ManualOverrideModal = ({ row, open, onClose, onApply, isPatching }) => {
    const [action, setAction] = useState('override');

    React.useEffect(() => {
        if (open) setAction('override');
    }, [open]);

    if (!open) return null;

    const handleApply = () => {
        if (row && onApply) onApply(row, action);
    };

    return (
        <div style={modalOverlayStyle} onClick={isPatching ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="manual-override-title">
            <div style={modalContentStyle} onClick={e => e.stopPropagation()}>
                <h2 id="manual-override-title" style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
                    Movement Report Action
                </h2>
                <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280' }}>
                    Choose an action for <strong>{row?.name || 'this booking'}</strong>.
                </p>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="movementAction" checked={action === 'override'} onChange={() => setAction('override')} />
                        Manual Override (set as pre-advised)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="movementAction" checked={action === 'ignore'} onChange={() => setAction('ignore')} />
                        Ignore (exclude from report)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="movementAction" checked={action === 'duplicate'} onChange={() => setAction('duplicate')} />
                        Duplicate (mark as duplicate)
                    </label>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isPatching && (
                        <span style={{ marginRight: '8px', fontSize: '13px', color: '#6b7280' }}>Updating...</span>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isPatching}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: isPatching ? 'not-allowed' : 'pointer',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            opacity: isPatching ? 0.6 : 1,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={isPatching}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: isPatching ? 'not-allowed' : 'pointer',
                            backgroundColor: action === 'ignore' || action === 'duplicate' ? '#ef4444' : '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            opacity: isPatching ? 0.8 : 1,
                        }}
                    >
                        {isPatching ? 'Updating...' : action === 'ignore' ? 'Ignore' : action === 'duplicate' ? 'Mark Duplicate' : 'Apply'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * RawDataSection Component
 * Displays a table of movement records for either inbound or outbound flights
 *
 * @param {Object} props
 * @param {string} props.title - Section title (e.g., "Outbound" or "Inbound")
 * @param {string} props.direction - "outbound" or "inbound", determines which time column to show
 * @param {Array} props.rawData - Array of movement record objects
 * @param {(row: Object, patch: { movementOverride: boolean }) => void} [props.onPatchBooking] - Called when user applies manual override
 */
const CODE_ORDER = [
    'BDGR', 'BLND', 'DEAF', 'DPNA', 'LNYD', 'MAAS', 'MEDA', 'PETC', 'PREBOARDHS',
    'WCBD', 'WCHC', 'WCHP', 'WCHR', 'WCHS', 'WCLB', 'WCMP', 'WELLCHK',
    'SVAN', 'STCR', 'WCBW', 'PPOC', 'ESAN', 'OXYG',
];

const exportToCsv = (rawData, direction, date, assistanceBreakdown) => {
    const isOutbound = direction === 'outbound';
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [];

    if (assistanceBreakdown?.length) {
        const countsByCode = assistanceBreakdown.reduce((acc, { assistanceCode, passengerCount }) => {
            acc[assistanceCode] = passengerCount;
            return acc;
        }, {});
        const orderedCodes = CODE_ORDER.filter((c) => countsByCode[c] != null);
        const otherCodes = Object.keys(countsByCode).filter((c) => !CODE_ORDER.includes(c)).sort();
        const displayCodes = [...orderedCodes, ...otherCodes];
        const total = Object.values(countsByCode).reduce((sum, n) => sum + n, 0);

        lines.push('Assistance Breakdown');
        lines.push(['Category', ...displayCodes, 'Total'].map(esc).join(','));
        lines.push(['Count', ...displayCodes.map((c) => countsByCode[c] ?? 0), total].map(esc).join(','));
        lines.push('');
    }

    lines.push(['#', 'Passenger ID', 'Name', 'Flight', isOutbound ? 'Destination' : 'Origin', isOutbound ? 'Departure' : 'Arrival', 'Booking Created', 'Time Diff', 'Pre-Advised', 'Reason'].map(esc).join(','));
    rawData.forEach((row, index) => {
        lines.push([
            index + 1,
            row.passengerId || '',
            row.name,
            row.flightNumber,
            row.secondAirport,
            isOutbound ? row.departureTime : row.arrivalTime,
            row.dateCreated,
            row.timeDifference || '-',
            row.preAdvised ? 'Yes' : 'No',
            row.reason || '',
        ].map(esc).join(','));
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${direction}-${date}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const RawDataSection = ({ title, direction, rawData, date, assistanceBreakdown, onPatchBooking, onOpenOverrideModal, passengerType }) => {
    const isOutbound = direction === 'outbound';
    const [isOpen, setIsOpen] = useState(false);

    // Handle empty or missing data case
    if (!rawData?.length) {
        return (
            <div>
                <p style={subTitleStyle}>{title}</p>
                <p style={{ fontSize: '13px', color: '#9ca3af' }}>No records.</p>
            </div>
        );
    }

    return (
        <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '12px',
            backgroundColor: '#fff',
        }}>
            {/* Collapsible section header with record count */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <span style={{
                    display: 'inline-block',
                    fontSize: '12px',
                    color: '#9ca3af',
                    transition: 'transform 0.2s ease',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                }}>
                    &#9654;
                </span>
                <p style={{ ...subTitleStyle, marginTop: 0, marginBottom: 0 }}>{title} ({rawData.length}) {"(" + date + ")"}</p>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); exportToCsv(rawData, direction, date, assistanceBreakdown); }}
                    style={{
                        marginLeft: 'auto',
                        padding: '4px 10px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                    }}
                >
                    Export CSV
                </button>
            </div>

            {!isOpen ? null : (

                <div className='mt-3'>
                    {assistanceBreakdown?.length > 0 && (
                        <MovementAssistanceBreakdown breakdown={assistanceBreakdown} title={title} />
                    )}

                    <p style={{ fontSize: '12px', fontWeight: 500, color: '#6b7280', margin: '0 0 8px' }}>
                        {passengerType} Passengers
                    </p>

                    <TableVirtuoso
                        style={{ height: '400px', marginTop: '10px' }}
                        data={rawData}
                        components={rawDataTableComponents}
                        fixedHeaderContent={() => (
                            <tr>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>#</th>
                                <th style={headerStyle}>Name</th>
                                <th style={headerStyle}>Passenger ID</th>
                                <th style={headerStyle}>Flight</th>
                                <th style={headerStyle}>{isOutbound ? 'Destination' : 'Origin'}</th>
                                <th style={headerStyle}>{isOutbound ? 'Departure' : 'Arrival'}</th>
                                <th style={headerStyle}>Booking Created</th>
                                <th style={headerStyle}>Time Diff</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>Pre-Advised</th>
                                <th style={headerStyle}>Reason</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>Action</th>
                            </tr>
                        )}
                        itemContent={(index, row) => (
                            <>
                                <td style={{ ...cellStyle, textAlign: 'center', color: '#9ca3af' }}>{index + 1}</td>
                                <td style={cellStyle}>{row.name}</td>
                                <td style={{ ...cellStyle, fontSize: '11px', wordBreak: 'break-all' }}>{row.passengerId ?? '–'}</td>
                                <td style={cellStyle}>{row.flightNumber}</td>
                                <td style={cellStyle}>{row.secondAirport}</td>
                                <td style={cellStyle}>{isOutbound ? row.departureTime : row.arrivalTime}</td>
                                <td style={cellStyle}>{row.dateCreated}</td>
                                <td style={cellStyle}>{row.timeDifference || '-'}</td>
                                <td style={{
                                    ...cellStyle,
                                    textAlign: 'center',
                                    backgroundColor: row.preAdvised ? '#dcfce7' : '#fee2e2',
                                    color: row.preAdvised ? '#166534' : '#991b1b',
                                }}>
                                    {row.preAdvised ? 'Yes' : 'No'}
                                </td>
                                <td style={cellStyle}>{row.reason || '-'}</td>
                                <td style={{ ...cellStyle, textAlign: 'center' }}>
                                    <button
                                        type="button"
                                        onClick={() => onOpenOverrideModal(row)}
                                        style={{
                                            padding: '6px 12px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            backgroundColor: '#3b82f6',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '6px',
                                        }}
                                    >
                                        Action
                                    </button>
                                </td>
                            </>
                        )}
                    />

                </div>)}
        </div>
    );
};

/** Column widths for the ignored passengers table */
const ignoredColumnWidths = ['5%', '15%', '15%', '12%', '10%', '15%', '28%'];

const IgnoredColGroup = () => (
    <colgroup>
        {ignoredColumnWidths.map((width, i) => (
            <col key={i} style={{ width }} />
        ))}
    </colgroup>
);

const VirtuosoIgnoredTable = React.forwardRef(({ children, style, ...rest }, ref) => (
    <table ref={ref} {...rest} style={{ ...tableStyle, ...style }}>
        <IgnoredColGroup />
        {children}
    </table>
));

const ignoredTableComponents = { Table: VirtuosoIgnoredTable };

const IgnoredPassengersSection = ({ rawData, date }) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!rawData?.length) return null;

    return (
        <div style={{
            border: '1px solid #fde68a',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '12px',
            backgroundColor: '#fffbeb',
        }}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <span style={{
                    display: 'inline-block',
                    fontSize: '12px',
                    color: '#9ca3af',
                    transition: 'transform 0.2s ease',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                }}>
                    &#9654;
                </span>
                <p style={{ ...subTitleStyle, marginTop: 0, marginBottom: 0, color: '#92400e' }}>
                    Ignored ({rawData.length}) {"(" + date + ")"}
                </p>
            </div>

            {!isOpen ? null : (
                <div className='mt-3'>
                    <TableVirtuoso
                        style={{ height: '300px', marginTop: '10px' }}
                        data={rawData}
                        components={ignoredTableComponents}
                        fixedHeaderContent={() => (
                            <tr>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>#</th>
                                <th style={headerStyle}>Name</th>
                                <th style={headerStyle}>Passenger ID</th>
                                <th style={headerStyle}>Flight</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>Direction</th>
                                <th style={headerStyle}>Booking Created</th>
                                <th style={headerStyle}>Flight Time</th>
                            </tr>
                        )}
                        itemContent={(index, row) => (
                            <>
                                <td style={{ ...cellStyle, textAlign: 'center', color: '#9ca3af' }}>{index + 1}</td>
                                <td style={cellStyle}>{row.name}</td>
                                <td style={{ ...cellStyle, fontSize: '11px', wordBreak: 'break-all' }}>{row.passengerId ?? '–'}</td>
                                <td style={cellStyle}>{row.flightNumber}</td>
                                <td style={{ ...cellStyle, textAlign: 'center' }}>
                                    <span style={{
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        backgroundColor: row.direction === 'outbound' ? '#dbeafe' : '#ede9fe',
                                        color: row.direction === 'outbound' ? '#1e40af' : '#5b21b6',
                                    }}>
                                        {row.direction === 'outbound' ? 'Outbound' : 'Inbound'}
                                    </span>
                                </td>
                                <td style={cellStyle}>{row.dateCreated}</td>
                                <td style={cellStyle}>
                                    {row.direction === 'outbound' ? row.departureTime : row.arrivalTime}
                                </td>
                            </>
                        )}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * MovementRawDataTable Component
 * Main component that displays raw movement data organized by date
 * Shows outbound, inbound, and ignored flight movements with pre-advice status
 *
 * @param {Object} props
 * @param {Array} props.dailyReports - Array of daily report objects
 * @param {Object|null} props.overrideRow - Row being overridden (controls modal visibility)
 * @param {(row: Object|null) => void} props.onOverrideRowChange - Called when opening/closing override modal
 * @param {(row: Object, action: string) => void} props.onPatchBooking - Called when user applies action (dispatches patch thunk)
 * @param {boolean} props.isPatching - Whether patch is in progress (show loader in modal)
 */
const MovementRawDataTable = ({ dailyReports, overrideRow, onOverrideRowChange, onPatchBooking, isPatching }) => {
    if (!dailyReports?.length) return null;

    const handleOpenOverrideModal = (row) => onOverrideRowChange(row);

    return (
        <div>

            {dailyReports.map((report) => (
                <div key={report.date} style={{ marginBottom: '24px' }}>

                    <RawDataSection
                        title="Outbound"
                        direction="outbound"
                        rawData={report.outbound?.rawData}
                        date={report.date}
                        assistanceBreakdown={report.outbound?.assistanceBreakdown}
                        onOpenOverrideModal={handleOpenOverrideModal}
                    />

                    <RawDataSection
                        title="Inbound"
                        direction="inbound"
                        rawData={report.inbound?.rawData}
                        date={report.date}
                        assistanceBreakdown={report.inbound?.assistanceBreakdown}
                        onOpenOverrideModal={handleOpenOverrideModal}
                    />

                    <IgnoredPassengersSection
                        rawData={report.ignored?.rawData}
                        date={report.date}
                    />
                </div>
            ))}

            <ManualOverrideModal
                row={overrideRow}
                open={!!overrideRow}
                onClose={() => onOverrideRowChange(null)}
                onApply={onPatchBooking}
                isPatching={isPatching}
            />
        </div>
    );
};

// #endregion

export default MovementRawDataTable;
